import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable } from "mobx";
import type { IUserDeactivationDataLayer } from "../types/IUserDeactivationDataLayer";
import { fromPromise } from "mobx-utils";
import { UserDeactivationDataLayerSymbol } from "./UserDeactivationDataLayerSymbol";
import type { IntervalOptionType } from "../types/IntervalOptionType";
import { INTERVAL_OPTIONS } from "./IntervalOptions";

@injectable()
export class UsersDeactivationSettingsStore {
  selectedOption: IntervalOptionType = INTERVAL_OPTIONS[0];
  isRunning = false;
  isSaving = false;
  nextRun = "";
  previousRun = "";

  constructor(@inject(UserDeactivationDataLayerSymbol) private userDeactivationDataLayer: IUserDeactivationDataLayer) {
    makeObservable(this, {
      selectedOption: observable.deep,
      isRunning: observable,
      isSaving: observable,
      nextRun: observable,
      previousRun: observable,
      setSelectedOption: action,
      setIsRunning: action,
      setIsSaving: action,
      runNow: action,
      saveSettings: action,
      init: computed,
    });
  }

  setSelectedOption(selectedOption: IntervalOptionType) {
    this.selectedOption = selectedOption;
  }

  setIsRunning(val: boolean) {
    this.isRunning = val;
  }

  setIsSaving(val: boolean) {
    this.isSaving = val;
  }

  get init() {
    return fromPromise(this.initData());
  }

  private async initData() {
    const { nextRun, previousRun, selectedOption } = await this.userDeactivationDataLayer.init();
    this.selectedOption = INTERVAL_OPTIONS.find((opt) => opt.value === selectedOption) ?? INTERVAL_OPTIONS[0];
    this.nextRun = nextRun;
    this.previousRun = previousRun;
  }

  async runNow() {
    this.setIsRunning(true);
    await this.userDeactivationDataLayer.runNow();
    this.setIsRunning(false);
  }

  async saveSettings() {
    this.setIsSaving(true);
    const res = await this.userDeactivationDataLayer.save(this.selectedOption);
    this.nextRun = res.nextRun ?? "";
    this.previousRun = res.previousRun ?? "";
    this.setIsSaving(false);
  }
}
